import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AutoEnhanceConfigPayload,
  AutoEnhanceConfigResponse,
} from '@/api/v4/project-auto-enhance.api';
import {
  disableAutoEnhance,
  getAutoEnhanceConfig,
  upsertAutoEnhanceConfig,
} from '@/api/v4/project-auto-enhance.api';
import { notify } from '@/ui/snackbar/notify';
import { useTranslation } from 'react-i18next';

const autoEnhanceConfigKey = (projectId?: string) => [
  'auto-enhance-config',
  projectId,
];

export const useAutoEnhanceConfigQuery = (projectId?: string) => {
  return useQuery<AutoEnhanceConfigResponse>({
    queryKey: autoEnhanceConfigKey(projectId),
    queryFn: () => getAutoEnhanceConfig(projectId as string),
    enabled: Boolean(projectId),
  });
};

export const useUpsertAutoEnhanceConfig = (projectId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('projects', { keyPrefix: 'projectOverview' });
  return useMutation({
    mutationFn: (payload: AutoEnhanceConfigPayload) => {
      if (!projectId) {
        throw new Error('Project id is required');
      }
      return upsertAutoEnhanceConfig(projectId, payload);
    },
    onError: error => {
      notify({
        message: t`autoEnhanceConfigError`,
      });
      console.error('Auto enhance config error:', error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: autoEnhanceConfigKey(projectId),
      });
      notify({
        message: t`autoEnhanceEnabled`,
      });
    },
  });
};

export const useDisableAutoEnhance = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error('Project id is required');
      }
      return disableAutoEnhance(projectId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: autoEnhanceConfigKey(projectId),
      });
    },
  });
};
